import React, { useState, useEffect } from 'react';
import { Chart } from "react-google-charts";
import Dropdown from 'react-bootstrap/Dropdown'; // Import Dropdown from react-bootstrap

function ParameterSensitivityChart(props) {

    const [parameter, setParameter] = useState(props.parameter || null);
    const [yName, setYName] = useState("cagr");
    const [zName, setZName] = useState("maxDrawdown");
    const [averages, setAverages] = useState(null);

    useEffect(() => {
        (async function () {
            setParameter(props.parameter);
        })();
    }, [props.parameter]);

    useEffect(() => {
        if (props.performanceArray && props.performanceArray.length > 0 && yName && zName) {
            const yValues = props.performanceArray.map(p => p[yName] || 0);
            const zValues = props.performanceArray.map(p => p[zName] || 0);

            const yTotal = yValues.reduce((sum, v) => sum + v, 0);
            const zTotal = zValues.reduce((sum, v) => sum + v, 0);

            setAverages({
                yAvg: yTotal / props.performanceArray.length,
                zAvg: zTotal / props.performanceArray.length,
                yMin: Math.min(...yValues),
                yMax: Math.max(...yValues),
                zMin: Math.min(...zValues),
                zMax: Math.max(...zValues),
            });
        } else {
            setAverages(null);
        }
    }, [props.performanceArray, yName, zName]);

    function getChartData(performanceArray) {
        if (performanceArray && performanceArray.length && parameter && yName && zName) {
            let ret = [];
            ret.push(["Parameter", yName, { role: 'style' }, { role: 'annotation' }, zName]);

            let currentParamValue = props.performance && props.performance.config ? props.performance.config[parameter] : null;

            performanceArray.filter(p => p.config).sort((l, r) => { return l.config[parameter] - r.config[parameter] }).forEach((p) => {
                let paramValue = p.config[parameter];
                let yValue = p[yName];
                let zValue = p[zName];
                
                let style = null;
                let annotation = null;
                if (currentParamValue !== null && paramValue === currentParamValue) {
                    style = 'color: #ff9900';
                    annotation = 'Current';
                }

                ret.push([paramValue, yValue, style, annotation, zValue]);
            });
            return ret;
        } else {
            return [];
        }
    }

    function getParameters(performanceArray) {
        if (performanceArray && performanceArray.length) {
            console.log("Performance array", performanceArray);
            let config = performanceArray[0].config;
            if (config)
                return Object.keys(config);
            else
                return [];
        } else {
            return [];
        }
    }

    function getValueNames(performanceArray) {
        if (performanceArray && performanceArray.length) {
            return ["cagr", "winningPercentage", "netProfit", "winLossRatio", "avgProfit", "maxDrawdown", "maxTradeDrawdown", "maxDrawdown", "avgTradeMaxDrawdown", "netExposurePercent", "avgDurationInBars", "avgLoss", "maxLoss", "tradeCount", "sharpeRatio"];
        } else {
            return [];
        }
    }

    const getChartOptions = () => {
        const baseOptions = {
            chartArea: { width: '80%', height: '75%' },
            seriesType: 'bars',
            legend: 'none',
            bar: { groupWidth: '75%' },
            colors: ['#3366cc', '#dc3912'],
            series: {
                1: { type: 'line', targetAxisIndex: 1 }
            },
            hAxis: {
                title: parameter,
                slantedText: true,
                slantedTextAngle: 30
            }
        };

        if (!averages) {
            return baseOptions;
        }

        const yRange = averages.yMax - averages.yMin;
        const yPadding = yRange === 0 ? Math.abs(averages.yMax * 0.1) || 1 : yRange * 0.1;
        const zRange = averages.zMax - averages.zMin;
        const zPadding = zRange === 0 ? Math.abs(averages.zMax * 0.1) || 1 : zRange * 0.1;

        return {
            ...baseOptions,
            vAxes: {
                0: { // Left axis
                    title: yName,
                    viewWindow: {
                        min: averages.yMin - yPadding,
                        max: averages.yMax + yPadding,
                    }
                },
                1: { // Right axis
                    title: zName,
                    viewWindow: {
                        min: averages.zMin - zPadding,
                        max: averages.zMax + zPadding,
                    }
                }
            }
        };
    };


    return (
        <div>
            {
                props.performanceArray && props.performanceArray.length ?
                    <>
                        <table width="100%">
                            <tr>
                                <td>
                                    <Dropdown >
                                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                                            Parameter: {parameter}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {getParameters(props.performanceArray).map((param, index) => (
                                                <Dropdown.Item key={index} eventKey={param} onClick={() => setParameter(param)}>
                                                    {param}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                                <td>
                                    <Dropdown >
                                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                                            Left Axis: {yName}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {getValueNames(props.performanceArray).map((name, index) => (
                                                <Dropdown.Item key={index} eventKey={name} onClick={() => setYName(name)}>
                                                    {name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                                <td>
                                    <Dropdown >
                                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                                            Right Axis: {zName}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {getValueNames(props.performanceArray).map((name, index) => (
                                                <Dropdown.Item key={index} eventKey={name} onClick={() => setZName(name)}>
                                                    {name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            </tr>
                        </table>

                        {averages ?
                            <div style={{ padding: '10px', textAlign: 'center' }}>
                                <table style={{ margin: 'auto' }}>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{yName}</th>
                                            <th>{zName}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ textAlign: 'right', paddingRight: '10px' }}><b>Average:</b></td>
                                            <td>{averages.yAvg.toFixed(2)}</td>
                                            <td>{averages.zAvg.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'right', paddingRight: '10px' }}><b>Minimum:</b></td>
                                            <td>{averages.yMin.toFixed(2)}</td>
                                            <td>{averages.zMin.toFixed(2)}</td>
                                        </tr>
                                        {props.performance && props.performance.config && parameter &&
                                            <tr>
                                                <td style={{ textAlign: 'right', paddingRight: '10px' }}><b>Current ({props.performance.config[parameter]}):</b></td>
                                                <td>{props.performance[yName] !== undefined ? props.performance[yName].toFixed(2) : 'N/A'}</td>
                                                <td>{props.performance[zName] !== undefined ? props.performance[zName].toFixed(2) : 'N/A'}</td>
                                            </tr>}
                                    </tbody>
                                </table>
                            </div>
                            : null}

                        <Chart
                            chartType="ComboChart"
                            width={"100%"}
                            data={getChartData(props.performanceArray)}
                            options={getChartOptions()}
                            chartPackages={["corechart", "controls"]}
                        />
                    </>
                    :
                    null
            }
        </div >);
}

export default ParameterSensitivityChart;